import { getIdToken } from '../config/msalInstance';

/**
 * Returns the request headers carrying the authenticated user's identity.
 *
 * Primary auth is the Azure AD B2C ID token as a Bearer credential — the
 * backend (agent-authorizer) validates its signature against the B2C JWKS, so
 * this is not spoofable. During the backend's transitional `legacy` rollout the
 * X-User-* headers are still sent as a fallback (the backend ignores them once
 * it flips to strict). The token itself is acquired fresh from MSAL and is
 * never persisted by this app.
 *
 * This is async because acquiring/refreshing the token via MSAL is async.
 *
 * @returns {Promise<Record<string,string>>}
 */
export async function getAuthHeaders() {
    const base = { 'Content-Type': 'application/json' };

    // Bearer token (verifiable identity). Null when not signed in.
    const token = await getIdToken();
    if (token) {
        base.Authorization = `Bearer ${token}`;
    }

    // Transitional X-User-* fallback (legacy backend rollout). Ignored by the
    // backend in strict mode. Kept from the localStorage session so behavior is
    // unchanged until strict cutover.
    try {
        const raw = localStorage.getItem('session');
        if (raw) {
            const session = JSON.parse(raw);
            base['X-User-Id'] = String(session.person_id || 'anonymous');
            base['X-User-Email'] = session.email || '';
            // Los headers HTTP solo transportan ASCII de forma segura; un nombre
            // con acentos o ñ (p. ej. "García") se corrompe en tránsito. Se
            // envía percent-encoded con encodeURIComponent para preservarlo.
            // IMPORTANTE: el backend DEBE hacer decodeURIComponent sobre este
            // header para recuperar el nombre original ("Garc%C3%ADa" → "García").
            base['X-User-Name'] = encodeURIComponent(session.userName || '');
        } else {
            base['X-User-Id'] = 'anonymous';
        }
    } catch {
        base['X-User-Id'] = 'anonymous';
    }

    return base;
}
